import { useQuery, useQueryClient } from "@tanstack/react-query"
import { FileSignature, Plus } from "lucide-react"
import { useState } from "react"

import { api } from "@/api/cliente"
import { NuevoContrato } from "@/componentes/contratos/NuevoContrato"
import { type Contrato, TarjetaContrato } from "@/componentes/contratos/TarjetaContrato"

/**
 * Los contratos de una persona.
 *
 * ⚠ **Emitir CONGELA el texto, y la pantalla lo dice antes de hacerlo.** El
 * descriptivo de cargo se calcula en vivo desde los procesos; un contrato firmado
 * tiene que seguir diciendo lo que decía. Sin avisarlo, quien emite creería que el
 * contrato se actualiza solo, y lo descubriría el día que haga falta demostrar qué
 * se pactó.
 *
 * ⚠ **Emitir es irreversible, y por eso pide confirmación aparte.** No hay «volver a
 * borrador»: existe una copia firmada fuera del sistema y las dos versiones tienen
 * que coincidir. Si lo emitido está mal, se anula y se hace otro — que es lo que se
 * haría con papel.
 *
 * ⚠ **Se enseñan TODOS, incluidos los cerrados.** El historial contractual es lo que
 * hay que mostrar el día de una reclamación; esconder lo terminado dejaría la ficha
 * contando media historia.
 *
 * Cada contrato y sus acciones viven en `TarjetaContrato`; esto es la lista.
 */

export function ContratosDePersona({
  personaId,
  cargoId,
}: {
  personaId: number
  cargoId: number | null
}) {
  const clienteQuery = useQueryClient()
  const [creando, setCreando] = useState(false)

  const contratos = useQuery({
    queryKey: ["contratos", personaId],
    queryFn: () => api.get<Contrato[]>(`/api/v1/personas/${personaId}/contratos`),
  })

  /** Tras emitir o cerrar cambian las dos cosas: la fila y lo que su vista previa
   *  compara contra ella. */
  function refrescar() {
    clienteQuery.invalidateQueries({ queryKey: ["contratos", personaId] })
    clienteQuery.invalidateQueries({ queryKey: ["contrato-previa"] })
  }

  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <h3 className="rotulo flex-1">Contratos</h3>
        <button
          type="button"
          onClick={() => setCreando(true)}
          aria-haspopup="dialog"
          data-testid="nuevo-contrato"
          className="boton-secundario h-8 px-2 text-xs"
        >
          <Plus size={14} aria-hidden={true} />
          Nuevo
        </button>
      </div>

      {contratos.isPending && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando contratos…
        </p>
      )}

      {contratos.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudieron cargar los contratos.
        </p>
      )}

      {contratos.data?.length === 0 && (
        <div
          className="flex flex-col items-center gap-2 superficie py-8"
          style={{ color: "var(--fg-muted)" }}
        >
          <FileSignature size={20} aria-hidden={true} />
          <p className="text-sm">Sin contratos todavía.</p>
        </div>
      )}

      <ul className="space-y-2">
        {(contratos.data ?? []).map((c) => (
          <TarjetaContrato key={c.id} contrato={c} onCambio={refrescar} />
        ))}
      </ul>

      <NuevoContrato
        personaId={personaId}
        cargoId={cargoId}
        abierto={creando}
        onCerrar={() => setCreando(false)}
        onCreado={() => {
          setCreando(false)
          refrescar()
        }}
      />
    </section>
  )
}
